import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Tag, Button } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { BellOutlined } from '@ant-design/icons';
import { useWisecureQuery } from '@wisecure/api-client/hooks';
import { queryKeys } from '@/api/query-keys';
import { PageHeader } from '@/components/PageHeader';
import { SearchFilterBar } from '@/components/SearchFilterBar';
import { InterventionDetailModal } from '@/components/InterventionDetailModal';
import { NotificationPopup } from '@/components/NotificationPopup';
import { DocumentStatusTag } from '@/components/DocumentStatusTag';
import type {
  PaginatedResponse,
  DashboardUserListItem,
  DashboardSocietyListItem,
  DashboardInterventionListItem,
  NotRegisteredAgentItem,
} from '@/types/dashboard';

interface SearchResult {
  users?: DashboardUserListItem[];
  societies?: DashboardSocietyListItem[];
  interventions?: DashboardInterventionListItem[];
}

export function SearchPage() {
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState('');
  const [entityType, setEntityType] = useState<string>('users');
  const [roleFilter, setRoleFilter] = useState<string | undefined>();
  const [missingDocs, setMissingDocs] = useState(false);
  const [highRejection, setHighRejection] = useState(false);
  const [societyTypeFilter, setSocietyTypeFilter] = useState<string | undefined>();
  const [societyStatusFilter, setSocietyStatusFilter] = useState<string | undefined>();
  const [missingSiret, setMissingSiret] = useState(false);
  const [refusedDocs, setRefusedDocs] = useState(false);
  const [interventionStatusFilter, setInterventionStatusFilter] = useState<string | undefined>();
  const [triggered, setTriggered] = useState(false);
  const [selectedInterventionId, setSelectedInterventionId] = useState<string | null>(null);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);

  const handleSearch = useCallback(() => {
    setTriggered(true);
  }, []);

  const { data: searchResults, isLoading: searchLoading } = useWisecureQuery<SearchResult>(
    queryKeys.search(searchText, entityType),
    (client) =>
      client.dashboard.dashboardControllerSearch({
        query: searchText,
        entityType: entityType as 'users' | 'societies' | 'interventions' | 'not-registered-agents',
      }) as unknown as Promise<SearchResult>,
    { enabled: triggered && searchText.length > 0 },
  );

  const { data: usersData, isLoading: usersLoading } = useWisecureQuery<
    PaginatedResponse<DashboardUserListItem>
  >(
    ['dashboard-users-filtered', roleFilter ?? '', String(missingDocs), String(highRejection)],
    (client) =>
      client.dashboard.dashboardControllerListUsers({
        role: roleFilter as 'CLIENT' | 'AGENT' | 'SOCIETY_OWNER' | 'ADMIN' | undefined,
        missingDocs: missingDocs || undefined,
        highRejection: highRejection || undefined,
      }) as unknown as Promise<PaginatedResponse<DashboardUserListItem>>,
    { enabled: entityType === 'users' && !searchText },
  );

  const { data: societiesData, isLoading: societiesLoading } = useWisecureQuery<
    PaginatedResponse<DashboardSocietyListItem>
  >(
    ['dashboard-societies-filtered', societyTypeFilter ?? '', societyStatusFilter ?? '', String(missingSiret), String(refusedDocs)],
    (client) =>
      client.dashboard.dashboardControllerListSocieties({
        type: societyTypeFilter as 'SECURITY' | 'INSTALLER' | 'DISTRIBUTOR' | undefined,
        status: societyStatusFilter as 'PENDING' | 'VALIDATED' | 'REJECTED' | 'WAITING_ON_INFORMATION' | undefined,
        missingSiret: missingSiret || undefined,
        refusedDocs: refusedDocs || undefined,
      }) as unknown as Promise<PaginatedResponse<DashboardSocietyListItem>>,
    { enabled: entityType === 'societies' && !searchText },
  );

  const { data: interventionsData, isLoading: interventionsLoading } = useWisecureQuery<
    PaginatedResponse<DashboardInterventionListItem>
  >(
    ['dashboard-interventions-filtered', interventionStatusFilter ?? ''],
    (client) =>
      client.dashboard.dashboardControllerListInterventions({
        status: interventionStatusFilter as 'REQUESTED' | 'WAITING_FOR_AGENT_ACCEPTATION' | 'ACCEPTED' | 'PAYMENT_IN_PROGRESS' | 'PAYMENT_DONE' | 'PAYMENT_FAILED' | 'PAYMENT_TIMED_OUT' | 'AGENT_MOVING' | 'INSPECTION_IN_PROGRESS' | 'NO_EFFRACTION' | 'EFFRACTION' | 'NO_SURVEILLANCE' | 'SURVEILLANCE_IN_PROGRESS' | 'SURVEILLANCE_DONE' | 'REPORT_IN_PROGRESS' | 'REJECTED' | 'CANCELED' | 'HIDDEN' | 'DONE' | 'PRICE_PROPOSED' | 'PRICE_ACCEPTED' | 'SURVEILLANCE_PAYMENT_IN_PROGRESS' | 'SURVEILLANCE_PAYMENT_DONE' | 'SURVEILLANCE_PAYMENT_FAILED' | 'SURVEILLANCE_PAYMENT_TIMED_OUT' | undefined,
      }) as unknown as Promise<PaginatedResponse<DashboardInterventionListItem>>,
    { enabled: entityType === 'interventions' && !searchText },
  );

  const { data: agentsData, isLoading: agentsLoading } = useWisecureQuery<NotRegisteredAgentItem[]>(
    queryKeys.dashboardNotRegisteredAgents,
    (client) =>
      client.dashboard.dashboardControllerListNotRegisteredAgents({}) as unknown as Promise<NotRegisteredAgentItem[]>,
    { enabled: entityType === 'not-registered-agents' && !searchText },
  );

  const isLoading = searchText
    ? searchLoading
    : entityType === 'users'
      ? usersLoading
      : entityType === 'societies'
        ? societiesLoading
        : entityType === 'interventions'
          ? interventionsLoading
          : agentsLoading;

  const getData = (): Record<string, unknown>[] => {
    if (searchText && searchResults) {
      switch (entityType) {
        case 'users':
          return (searchResults.users ?? []) as unknown as Record<string, unknown>[];
        case 'societies':
          return (searchResults.societies ?? []) as unknown as Record<string, unknown>[];
        case 'interventions':
          return (searchResults.interventions ?? []) as unknown as Record<string, unknown>[];
        default:
          return [];
      }
    }
    switch (entityType) {
      case 'users':
        return (usersData?.data ?? []) as unknown as Record<string, unknown>[];
      case 'societies':
        return (societiesData?.data ?? []) as unknown as Record<string, unknown>[];
      case 'interventions':
        return (interventionsData?.data ?? []) as unknown as Record<string, unknown>[];
      case 'not-registered-agents':
        return (agentsData ?? []) as unknown as Record<string, unknown>[];
      default:
        return [];
    }
  };

  const data = getData();

  const userColumns: ColumnsType<Record<string, unknown>> = [
    {
      title: 'Statut doc',
      dataIndex: 'emailVerified',
      key: 'emailVerified',
      render: (status: unknown) => <DocumentStatusTag status={String(status ?? '')} />,
    },
    {
      title: 'Nom complet',
      key: 'name',
      render: (_: unknown, r: Record<string, unknown>) =>
        [r.firstName, r.lastName].filter(Boolean).join(' ') || '—',
    },
    {
      title: 'Type',
      dataIndex: 'role',
      key: 'role',
      render: (r: unknown) => <Tag>{String(r)}</Tag>,
    },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    {
      title: 'Téléphone',
      key: 'phone',
      render: (_: unknown, r: Record<string, unknown>) => {
        const phone = r.phone as Record<string, unknown> | undefined;
        return phone ? `${String(phone.countryCode ?? '')}${String(phone.phoneNumber ?? '')}` : '—';
      },
    },
  ];

  const societyColumns: ColumnsType<Record<string, unknown>> = [
    {
      title: 'Validé',
      dataIndex: 'status',
      key: 'status',
      render: (s: unknown) => <DocumentStatusTag status={String(s ?? '')} />,
    },
    { title: 'Nom', dataIndex: 'name', key: 'name' },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (t: unknown) => <Tag>{String(t)}</Tag>,
    },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'Téléphone', dataIndex: 'phone', key: 'phone' },
    {
      title: 'Siège social',
      key: 'hq',
      render: (_: unknown, r: Record<string, unknown>) => {
        const hq = r.headQuarterAddress as Record<string, unknown> | undefined;
        return hq
          ? [hq.number, hq.street, hq.zip, hq.city].filter(Boolean).join(' ')
          : '—';
      },
    },
  ];

  const interventionColumns: ColumnsType<Record<string, unknown>> = [
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      render: (s: unknown) => <Tag>{String(s)}</Tag>,
    },
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (d: unknown) => new Date(String(d)).toLocaleString('fr-FR'),
    },
    {
      title: 'Client',
      key: 'client',
      render: (_: unknown, r: Record<string, unknown>) =>
        String(r.clientName ?? r.clientEmail ?? '—'),
    },
    {
      title: 'Agent',
      key: 'agent',
      render: (_: unknown, r: Record<string, unknown>) =>
        String(r.agentName ?? '—'),
    },
    {
      title: 'Société',
      key: 'society',
      render: (_: unknown, r: Record<string, unknown>) =>
        String(r.societyName ?? '—'),
    },
  ];

  const agentColumns: ColumnsType<Record<string, unknown>> = [
    { title: 'Nom complet', dataIndex: 'fullName', key: 'fullName' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    {
      title: 'Téléphone',
      key: 'phone',
      render: (_: unknown, r: Record<string, unknown>) => {
        const phone = r.phone as Record<string, unknown> | undefined;
        return phone ? `${String(phone.countryCode ?? '')}${String(phone.phoneNumber ?? '')}` : '—';
      },
    },
  ];

  const getColumns = (): ColumnsType<Record<string, unknown>> => {
    switch (entityType) {
      case 'users':
        return userColumns;
      case 'societies':
        return societyColumns;
      case 'interventions':
        return interventionColumns;
      case 'not-registered-agents':
        return agentColumns;
      default:
        return [];
    }
  };

  const handleRowClick = (record: Record<string, unknown>) => {
    const recordId = String(record.id ?? '');
    switch (entityType) {
      case 'users':
        navigate(`/users/${recordId}`);
        break;
      case 'not-registered-agents':
        navigate(`/agents/${recordId}`);
        break;
      case 'societies':
        navigate(`/societies/${recordId}`);
        break;
      case 'interventions':
        setSelectedInterventionId(recordId);
        break;
    }
  };

  return (
    <div>
      <PageHeader
        title="Recherche"
        subtitle={`Nombre de résultats : ${data.length}`}
        extra={
          entityType === 'users' && selectedUserIds.length > 0 ? (
            <Button icon={<BellOutlined />} onClick={() => setNotificationOpen(true)}>
              Envoyer notification ({selectedUserIds.length})
            </Button>
          ) : undefined
        }
      />

      <SearchFilterBar
        searchText={searchText}
        onSearchTextChange={setSearchText}
        entityType={entityType}
        onEntityTypeChange={(v) => {
          setEntityType(v);
          setTriggered(false);
        }}
        roleFilter={roleFilter}
        onRoleFilterChange={setRoleFilter}
        missingDocs={missingDocs}
        onMissingDocsChange={setMissingDocs}
        highRejection={highRejection}
        onHighRejectionChange={setHighRejection}
        societyTypeFilter={societyTypeFilter}
        onSocietyTypeFilterChange={setSocietyTypeFilter}
        societyStatusFilter={societyStatusFilter}
        onSocietyStatusFilterChange={setSocietyStatusFilter}
        missingSiret={missingSiret}
        onMissingSiretChange={setMissingSiret}
        refusedDocs={refusedDocs}
        onRefusedDocsChange={setRefusedDocs}
        interventionStatusFilter={interventionStatusFilter}
        onInterventionStatusFilterChange={setInterventionStatusFilter}
        onSearch={handleSearch}
      />

      <div style={{ marginTop: 24 }}>
        <Table<Record<string, unknown>>
          columns={getColumns()}
          dataSource={data}
          loading={isLoading}
          rowKey="id"
          onRow={(record) => ({
            onClick: () => handleRowClick(record),
            style: { cursor: 'pointer' },
          })}
          rowSelection={
            entityType === 'users'
              ? {
                  selectedRowKeys: selectedUserIds,
                  onChange: (keys) => setSelectedUserIds(keys as string[]),
                }
              : undefined
          }
          pagination={{ pageSize: 20 }}
        />
      </div>

      <InterventionDetailModal
        interventionId={selectedInterventionId}
        onClose={() => setSelectedInterventionId(null)}
      />

      <NotificationPopup
        open={notificationOpen}
        onClose={() => setNotificationOpen(false)}
        userIds={selectedUserIds}
      />
    </div>
  );
}
